import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CornerDownLeftIcon, FileTextIcon, ListChecksIcon, SearchIcon, UserIcon, WrenchIcon, XIcon } from 'lucide-react';
import { ProCustomer, ProJob, Quote } from '../types/pro';
import { QualityReport } from '../types/quality';
import { SearchResult, SearchResultKind, groupResults, searchEverything } from '../utils/search';
interface TopBarProps {
  customers: ProCustomer[];
  jobs: ProJob[];
  quotes: Quote[];
  qualityReports: QualityReport[];
  isAdmin: boolean;
  onSelectResult: (result: SearchResult) => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const KIND_ICON: Record<SearchResultKind, typeof UserIcon> = {
  customer: UserIcon,
  job: WrenchIcon,
  quote: FileTextIcon,
  quality: ListChecksIcon,
  page: CornerDownLeftIcon
};
export function TopBar({
  customers,
  jobs,
  quotes,
  qualityReports,
  isAdmin,
  onSelectResult
}: TopBarProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const results = useMemo(() => searchEverything({
    query,
    customers,
    jobs,
    quotes,
    qualityReports,
    isAdmin
  }), [query, customers, jobs, quotes, qualityReports, isAdmin]);
  const grouped = useMemo(() => groupResults(results), [results]);

  // Reset the highlighted row whenever the result set changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Cmd/Ctrl+K focuses search from anywhere.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Close the dropdown on an outside click.
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);
  const commit = (result: SearchResult) => {
    onSelectResult(result);
    setQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  };
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(i => (i + 1) % results.length);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(i => (i - 1 + results.length) % results.length);
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      commit(results[activeIndex]);
    }
  };
  const showDropdown = isOpen && query.trim().length >= 2;
  let flatIndex = -1;
  return <header data-app-chrome className="sticky top-0 z-30 border-b border-stone-200 bg-white/90 backdrop-blur" data-id="element-956" data-mp="2laspftlprr3t">
      <div className="flex items-center gap-3 px-4 py-3 sm:px-6 lg:px-10" data-id="element-957" data-mp="2ucs7mp8gt18c">
        <span className="flex-shrink-0 text-base font-bold tracking-tight text-stone-900 lg:hidden" data-id="element-958" data-mp="2ucii12rc0w36">
          Lykke-EL
        </span>

        <div ref={containerRef} className="relative min-w-0 flex-1 lg:max-w-xl" data-id="element-959" data-mp="342vbxll1e1lz">
          <label className="relative block" data-id="element-960" data-mp="344y0002mpko2">
            <span className="sr-only" data-id="element-961" data-mp="390n2oc9y3hol">Søg</span>
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" data-id="element-962" data-mp="393j0m0lsm6d1" />
            <input ref={inputRef} value={query} onChange={e => {
            setQuery(e.target.value);
            setIsOpen(true);
          }} onFocus={() => setIsOpen(true)} onKeyDown={handleKeyDown} placeholder="Søg kunder, opgaver, tilbud…" role="combobox" aria-expanded={showDropdown} aria-controls="global-search-results" className="w-full rounded-lg border border-stone-200 bg-stone-50 py-2 pl-9 pr-16 text-sm text-stone-900 outline-none transition-colors placeholder:text-stone-400 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-brand-500" data-id="element-963" data-mp="3i23acc9dl5p4" />
            {query ? <button onClick={() => {
            setQuery('');
            inputRef.current?.focus();
          }} aria-label="Ryd søgning" className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700" data-id="element-964" data-mp="9oz0nmwcy26i">
                <XIcon className="h-4 w-4" data-id="element-965" data-mp="1okicp4n5bxd4" />
              </button> : <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded border border-stone-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-stone-400 sm:block" data-id="element-966" data-mp="1smv8b086iew6">
                ⌘K
              </kbd>}
          </label>

          <AnimatePresence data-id="element-967" data-mp="27b9jpzjnvboc">
            {showDropdown && <motion.div id="global-search-results" role="listbox" initial={{
            opacity: 0,
            y: -6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.15,
            ease: EASE
          }} className="absolute left-0 right-0 top-full z-40 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border border-stone-200 bg-white py-1.5 shadow-lg" data-id="element-968" data-mp="27brojkke2y7v">
                {results.length === 0 ? <p className="px-4 py-6 text-center text-sm text-stone-500" data-id="element-969" data-mp="2v2x1nryy3ceh">
                    Ingen resultater for “{query}”
                  </p> : grouped.map(group => <div key={group.group} data-id="element-970" data-mp="2uymzz3uoy3bb">
                      <h3 className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-971" data-mp="2uxbrwru7q9ee">
                        {group.group}
                      </h3>
                      <ul data-id="element-972" data-mp="2lz06d487e1st">
                        {group.items.map(result => {
                  flatIndex += 1;
                  const isActive = flatIndex === activeIndex;
                  const Icon = KIND_ICON[result.kind];
                  return <li key={result.id} role="option" aria-selected={isActive} data-id="element-973" data-mp="279ty6qre118s">
                              <button onMouseEnter={() => setActiveIndex(results.indexOf(result))} onClick={() => commit(result)} className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${isActive ? 'bg-brand-50' : 'hover:bg-stone-50'}`} data-id="element-974" data-mp="237yvbhtt6fsw">
                                <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${isActive ? 'bg-brand-100 text-brand-700' : 'bg-stone-100 text-stone-500'}`} data-id="element-975" data-mp="rotdt4f68z7l">
                                  <Icon className="h-4 w-4" data-id="element-976" data-mp="8stflbvlozfk" />
                                </span>
                                <span className="min-w-0 flex-1" data-id="element-977" data-mp="37gpjcfj9zavs">
                                  <span className="block truncate text-sm font-medium text-stone-900" data-id="element-978" data-mp="3cfk0ld1lgrds">
                                    {result.title}
                                  </span>
                                  {result.subtitle && <span className="block truncate text-xs text-stone-500" data-id="element-979" data-mp="20w0y2237xff4">
                                      {result.subtitle}
                                    </span>}
                                </span>
                                {result.reference && <span className="flex-shrink-0 font-mono text-xs text-stone-400" data-id="element-980" data-mp="2jt27q07pw3pe">
                                    {result.reference}
                                  </span>}
                              </button>
                            </li>;
                })}
                      </ul>
                    </div>)}
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </header>;
}